import { useState } from 'react';
import { esTelefonoValido } from '../lib/telefono.js';
import { precio } from '../lib/formato.js';
import { Resumen } from '../componentes/Resumen.jsx';
import { Alerta } from '../componentes/Iconos.jsx';

// La misma forma que valida el backend: algo, arroba, algo, punto, algo. No
// hace falta más — validar emails a fondo con una expresión regular es una
// carrera perdida, y el que se equivoca en serio se entera cuando no le llega.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * La seña, dicha antes de apretar el botón.
 *
 * Nadie tiene que descubrir que hay que pagar cuando ya está en Mercado Pago.
 * Van las tres cosas que hacen falta para decidir: cuánto, que es una parte y
 * no el total, y qué pasa si después cancela —con las palabras del negocio,
 * que es el que pone la regla.
 */
function Senia({ centavos, politica, precioTotal, modo }) {
  const esTotal = modo === 'total';

  return (
    <div className="senia-aviso">
      <p className="senia-monto">
        {esTotal
          ? <>Para reservar pagás <strong>{precio(centavos)}</strong>, el turno completo</>
          : <>Para reservar pagás <strong>{precio(centavos)}</strong> de seña</>}
        {!esTotal && precioTotal > centavos && <span className="senia-resto">
          {' '}· el resto ({precio(precioTotal - centavos)}) lo pagás en el lugar
        </span>}
      </p>
      <p className="senia-politica">{politica}</p>
      <p className="senia-donde">
        Se paga en Mercado Pago, con tarjeta o dinero en cuenta. La plata va
        directo al negocio.
      </p>
    </div>
  );
}

export function PasoDatos({
  seleccion, senia, timezone, enviando, avisoOcupado, onReservar, vocabulario
}) {
  const [nombre, setNombre] = useState('');
  const [telefono, setTelefono] = useState('');
  const [email, setEmail] = useState('');
  const [errores, setErrores] = useState({});
  const [tocado, setTocado] = useState({});

  function validar() {
    const nuevos = {};
    if (nombre.trim().length < 2) nuevos.nombre = 'Poné tu nombre completo.';
    // Se valida acá para no hacerle esperar un viaje al servidor por un
    // número mal escrito. El backend vuelve a validar por su cuenta.
    if (!esTelefonoValido(telefono)) {
      nuevos.telefono = 'Revisá el número: poné característica y número, por ejemplo 3491 123456.';
    }
    // Vacío y mal escrito son dos problemas distintos y se arreglan distinto:
    // uno es "te lo salteaste" y el otro "fijate el que pusiste". Un mensaje
    // solo para los dos manda a revisar un campo que está en blanco.
    if (email.trim() === '') {
      nuevos.email = 'Poné tu email: ahí te mandamos la confirmación del turno.';
    } else if (!EMAIL_RE.test(email.trim())) {
      nuevos.email = 'Revisá el email: parece que le falta algo.';
    }
    setErrores(nuevos);
    return Object.keys(nuevos).length === 0;
  }

  function enviar(evento) {
    evento.preventDefault();
    setTocado({ nombre: true, telefono: true, email: true });
    if (validar()) {
      onReservar({
        nombre: nombre.trim(),
        telefono: telefono.trim(),
        email: email.trim().toLowerCase()
      });
    }
  }

  const mostrar = (campo) => (tocado[campo] ? errores[campo] : null);

  return (
    <form onSubmit={enviar} noValidate>
      <Resumen {...seleccion} timezone={timezone} vocabulario={vocabulario} />

      {avisoOcupado && (
        <div className="aviso" role="alert">
          <Alerta />
          <p>{avisoOcupado}</p>
        </div>
      )}

      <div className="campo">
        <label htmlFor="nombre">Tu nombre</label>
        <input
          id="nombre"
          name="nombre"
          type="text"
          autoComplete="name"
          placeholder="Martín Gómez"
          value={nombre}
          maxLength={120}
          aria-invalid={Boolean(mostrar('nombre'))}
          aria-describedby={mostrar('nombre') ? 'error-nombre' : undefined}
          onChange={(e) => setNombre(e.target.value)}
          onBlur={() => { setTocado((t) => ({ ...t, nombre: true })); validar(); }}
        />
        {mostrar('nombre') && <p id="error-nombre" className="campo-error">{errores.nombre}</p>}
      </div>

      <div className="campo">
        <label htmlFor="telefono">Tu WhatsApp</label>
        <input
          id="telefono"
          name="telefono"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          placeholder="3491 123456"
          value={telefono}
          maxLength={30}
          aria-invalid={Boolean(mostrar('telefono'))}
          aria-describedby={mostrar('telefono') ? 'error-telefono' : 'ayuda-telefono'}
          onChange={(e) => setTelefono(e.target.value)}
          onBlur={() => { setTocado((t) => ({ ...t, telefono: true })); validar(); }}
        />
        {mostrar('telefono')
          ? <p id="error-telefono" className="campo-error">{errores.telefono}</p>
          : <p id="ayuda-telefono" className="ayuda">Por si el negocio necesita ubicarte.</p>}
      </div>

      <div className="campo">
        <label htmlFor="email">Tu email</label>
        <input
          id="email"
          name="email"
          type="email"
          inputMode="email"
          autoComplete="email"
          autoCapitalize="none"
          spellCheck="false"
          placeholder="nombre@email.com"
          value={email}
          maxLength={160}
          aria-invalid={Boolean(mostrar('email'))}
          aria-describedby={mostrar('email') ? 'error-email' : 'ayuda-email'}
          onChange={(e) => setEmail(e.target.value)}
          onBlur={() => { setTocado((t) => ({ ...t, email: true })); validar(); }}
        />
        {mostrar('email')
          ? <p id="error-email" className="campo-error">{errores.email}</p>
          : (
            <p id="ayuda-email" className="ayuda">
              Ahí te mandamos la confirmación con la dirección y el horario, el
              recordatorio antes del turno, y el link por si necesitás cancelar.
            </p>
          )}
      </div>

      {senia && (
        <Senia
          centavos={senia.centavos}
          modo={senia.modo}
          politica={senia.politica}
          precioTotal={seleccion.servicio?.precio_centavos ?? 0}
        />
      )}

      <button type="submit" className="boton boton-primario" disabled={enviando}>
        {enviando
          ? (senia ? 'Abriendo el pago…' : 'Reservando…')
          : (senia ? `Pagar ${precio(senia.centavos)} y reservar` : 'Confirmar turno')}
      </button>
    </form>
  );
}
